import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { btn } from '../../utils/buttonStyles';
import EventAttendance, { getAvailableMembers } from '../../events/components/EventAttendance';
import EventInfo from '../../events/components/EventInfo';
import { getScoringComponent, getSportNameSync, generateGameName } from '../../scoring/scoringComponents';
import GameComponent from '../../scoring/GameComponent';
// No need to import game APIs - scoring components handle their own game operations
import { apiCall } from '../../services/apiUtil';
import { claimScorerRole } from '../../scoring/services/claimScorer';
import toast from 'react-hot-toast';
import { isSquadManager } from '../utils/squadRoles';

/**
 * CurrentEventTab - Self-contained tab for viewing current/upcoming event
 *
 * Props:
 * - voProvider: SquadDetailsProvider instance (required)
 *
 * This component is sport-agnostic. All sport-specific logic (game initialization,
 * loading, etc.) is handled by the respective scoring components (PokerScoring,
 * CricketScoring, PingPongScoring, etc.)
 */

const CurrentEventTab = ({ voProvider }) => {
  const { theme } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();

  // Guard against missing provider
  if (!voProvider) {
    return <div className="p-6 text-center text-gray-500">Loading...</div>;
  }

  // Get data from VO
  const group = voProvider.getGroup();
  const members = voProvider.getMembers();
  const groupId = group?.id;
  const isAdmin = isSquadManager(group?.userRole);

  // Debug: Log VO values

  // Local state
  const [currentEvent, setCurrentEvent] = useState(null);
  const [showScoring, setShowScoring] = useState(false);
  const [showScorerConfirm, setShowScorerConfirm] = useState(false);
  const [gameCommonId, setGameCommonId] = useState(null);
  const [eventGames, setEventGames] = useState([]);
  const [isScorer, setIsScorer] = useState(null);

  // Store refresh function from EventInfo
  const refreshEventsRef = useRef(null);

  // Track deleted game IDs to prevent VO from auto-reopening a just-deleted game
  const deletedGameIdRef = useRef(null);

  // Track the current event ID to detect event changes
  const prevEventIdRef = useRef(null);

  // Get only available event attendees (members who RSVP'd as available/join-late)
  const availableEventMembers = useMemo(() => {
    if (!currentEvent || !members) return [];
    return getAvailableMembers(members, currentEvent);
  }, [currentEvent, members]);

  // Lock attendance when any game for this event is actively in progress
  const isAttendanceLocked = useMemo(() => {
    return eventGames.some(g => g.status === 'in_progress' || g.status === 'active');
  }, [eventGames]);

  // At least one game is completed (triggers "Start Another" + "Complete Event" UI)
  const hasCompletedGame = useMemo(() => {
    return eventGames.some(g => g.status === 'completed');
  }, [eventGames]);


  // Clear gameId when event changes
  useEffect(() => {
    const currentEventId = currentEvent?.id || currentEvent?.eventData?.id;

    // If event changed (different event ID), clear gameId and close scoring
    if (prevEventIdRef.current !== null && currentEventId !== prevEventIdRef.current) {
      setGameCommonId(null);
      setShowScoring(false);

      // Remove gameId from URL using React Router
      setSearchParams(params => {
        const newParams = new URLSearchParams(params);
        newParams.delete('gameId');
        newParams.delete('openGame');
        return newParams;
      });
    }

    // Update the ref with current event ID
    prevEventIdRef.current = currentEventId;
  }, [currentEvent, setSearchParams]);

  // Auto-open scoring if gameId is in URL AND event is loaded
  useEffect(() => {
    const gameIdFromUrl = searchParams.get('gameId');
    const openGameFromUrl = searchParams.get('openGame') === 'true';

    // Only auto-open if we have both gameId in URL AND the event is loaded
    if (gameIdFromUrl && openGameFromUrl && currentEvent) {
      setGameCommonId(parseInt(gameIdFromUrl));
      setShowScoring(true);
    }
  }, [searchParams, currentEvent]);

  // Auto-open scoring if there's an active game in VO
  useEffect(() => {
    if (!voProvider || !currentEvent) return;

    const sportName = group?.sportType?.sportName?.toLowerCase();
    const sportGameMap = {
      'ping pong': 'pingpong',
      'table tennis': 'pingpong',
      'pingpong': 'pingpong',
      'cricket': 'cricket',
      'basketball': 'basketball',
      'tennis': 'tennis',
      'pickleball': 'pickleball',
      'poker': 'poker',
      'badminton': 'badminton'
    };

    const gameKey = sportGameMap[sportName];
    const activeGame = voProvider.vo.currentEvent.games?.[gameKey];
    const currentEventId = currentEvent?.id || currentEvent?.eventData?.id;


    // Auto-open scoring if there's an active game (any status except completed)
    // AND the game belongs to the current event (prevent stale game data)
    // Note: Only using standard game_common status values (enum)
    const activeStatuses = ['setup', 'player_selection', 'in_progress'];
    const gameMatchesEvent = activeGame?.eventId === currentEventId;

    if (activeGame && activeStatuses.includes(activeGame.status) && !showScoring && gameMatchesEvent) {
      // Don't auto-reopen a game that was just deleted (VO may still have stale data)
      if (deletedGameIdRef.current && activeGame.id === deletedGameIdRef.current) {
        return;
      }
      setGameCommonId(activeGame.id);
      setShowScoring(true);
    } else if (activeGame && !gameMatchesEvent) {
    }
  }, [voProvider, currentEvent, group, showScoring]);

  // Check backend for existing games when event loads (in case VO hasn't loaded yet)
  useEffect(() => {
    const checkForExistingGames = async () => {
      if (!currentEvent || showScoring) return;

      const currentEventId = currentEvent?.id || currentEvent?.eventData?.id;

      if (!currentEventId) return;

      // Use unified games endpoint
      const endpoint = '/games';

      try {
        const games = await apiCall(`${endpoint}/event/${currentEventId}`);
        setEventGames(games || []);

        // Find the most recent active game
        // Note: Only using standard game_common status values (enum)
        const activeStatuses = ['setup', 'player_selection', 'in_progress'];
        const activeGame = games.find(g => activeStatuses.includes(g.status));

        if (activeGame) {
          // Don't re-open a game that was just closed/errored (same guard as VO effect)
          if (deletedGameIdRef.current && activeGame.id === deletedGameIdRef.current) {
          } else {
            setGameCommonId(activeGame.id || activeGame.gameCommonId);
            setShowScoring(true);
          }
        } else {
        }
      } catch (error) {
        console.error('[CurrentEventTab] Error checking for existing games:', error);
      }
    };

    checkForExistingGames();
  }, [currentEvent, group, showScoring]);

  // Callback when event is loaded from EventInfo
  const handleEventLoaded = useCallback(async (event, refreshFn) => {
    setCurrentEvent(event);
    refreshEventsRef.current = refreshFn;
    // Auto-open logic is now handled by useEffect that watches VO
  }, []);

  // Callback when event is updated (from EventAttendance)
  const handleEventUpdated = useCallback(async (updatedEvent) => {
    // Use the fresh event data directly instead of re-fetching all events
    // This avoids backend filtering issues and is more efficient
    if (updatedEvent) {
      setCurrentEvent(updatedEvent);
      // Auto-open logic is now handled by useEffect that watches VO
    }
  }, []);

  // Complete the event without needing game data (called from EventInfo "Complete Event" button)
  const handleCompleteEvent = useCallback(async () => {
    const eventIdToUse = currentEvent?.eventData?.id || currentEvent?.id;
    if (!eventIdToUse) return;

    try {
      await apiCall(`/events/${eventIdToUse}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'completed' }),
      });

      if (refreshEventsRef.current) {
        await refreshEventsRef.current();
      }
      setCurrentEvent(null);
      setShowScoring(false);
      setGameCommonId(null);
      setEventGames([]);
    } catch (error) {
      console.error('[CurrentEventTab] Failed to complete event:', error);
    }
  }, [currentEvent]);

  // Handle game completion — defined here (not inside renderScoringComponent) to avoid stale closure
  const handleGameComplete = useCallback(async (gameData) => {
    const eventIdToUse = currentEvent?.eventData?.id || currentEvent?.id;

    if (!eventIdToUse) {
      console.error('[CurrentEventTab] No event ID available to complete');
      return;
    }

    try {
      await apiCall(`/events/${eventIdToUse}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'completed' }),
      });

      if (refreshEventsRef.current) {
        await refreshEventsRef.current();
      }

      setCurrentEvent(null);
      setShowScoring(false);
      setGameCommonId(null);
      setIsScorer(null);
    } catch (error) {
      console.error('[CurrentEventTab] Failed to update event status:', error);
    }
  }, [currentEvent]);

  // Show confirmation before starting scoring
  const handleStartScoring = useCallback(() => {
    setShowScorerConfirm(true);
  }, []);

  // Confirmed — create game and claim scorer
  const handleConfirmStartScoring = useCallback(async () => {
    setShowScorerConfirm(false);
    if (!currentEvent) {
      return;
    }

    const sportTypeId = group?.sportType?.id;
    const eventIdToUse = currentEvent?.eventData?.id || currentEvent?.id;

    if (!sportTypeId) {
      console.error('[CurrentEventTab] No sportTypeId available');
      return;
    }

    // Use unified games endpoint
    const endpoint = '/games';

    try {
      // Create game_common entry with initial status 'setup'
      const gameData = await apiCall(endpoint, {
        method: 'POST',
        body: JSON.stringify({
          eventId: eventIdToUse,
          sportTypeId: sportTypeId,
          status: 'setup',
          gameName: generateGameName(sportName),
        }),
      });

      // Claim scorer role for this game
      const claim = await claimScorerRole(gameData.id);
      if (!claim.claimed) {
        toast.error(`${claim.scorerName} is already scoring this game`);
      }
      setIsScorer(claim.claimed);

      deletedGameIdRef.current = null; // new game started — clear stale deletion guard
      setGameCommonId(gameData.id);
      setShowScoring(true);
    } catch (error) {
      console.error('[CurrentEventTab] Failed to create game:', error);
      // Show error to user - could add toast notification here
    }
  }, [currentEvent, group]);

  // Get the scoring component for rendering
  const renderScoringComponent = () => {
    const sportTypeId = group?.sportTypeId || group?.sportType?.id;
    const sportName = group?.sportType?.sportName || getSportNameSync(sportTypeId);
    const maxPlayersPerSide = group?.sportType?.maxPlayersPerSide ?? null;

    // Handler to close scoring and return to event view
    const handleCloseScoring = async () => {
      setIsScorer(null);
      // Remember the game ID being closed so the VO effect doesn't re-open it
      if (gameCommonId) deletedGameIdRef.current = gameCommonId;
      setShowScoring(false);
      setGameCommonId(null); // Clear game ID
      setCurrentEvent(null); // Clear current event to show "Create New Event"
      // Refresh events after closing scoring
      if (refreshEventsRef.current) {
        refreshEventsRef.current();
      }
    };

    // handleGameComplete is defined at component level (stable useCallback)

    return (
      <GameComponent
        sportType={sportName}
        sportTypeId={sportTypeId}
        maxPlayersPerSide={maxPlayersPerSide}
        gameCommonId={gameCommonId}
        groupId={groupId}
        members={availableEventMembers}
        event={currentEvent}
        onGameComplete={handleGameComplete}
        onCancel={handleCloseScoring}
        isScorer={isScorer}
        isAdmin={isAdmin}
      />
    );
  };

  const sportTypeId = group?.sportTypeId || group?.sportType?.id;
  const sportName = group?.sportType?.sportName || getSportNameSync(sportTypeId);

  return (
    <div className="space-y-6">
      {/* Event Attendance Table */}
      <EventAttendance
        voProvider={voProvider}
        event={currentEvent}
        onEventUpdated={handleEventUpdated}
        isGameActive={isAttendanceLocked}
      />

      {/* Event Details or Scoring */}
      {!showScoring ? (
        <EventInfo
          ownerType="group"
          ownerId={groupId}
          sportName={sportName}
          sportTypeId={sportTypeId}
          members={members}
          voProvider={voProvider}
          onEventLoaded={handleEventLoaded}
          onStartScoring={handleStartScoring}
          onCompleteEvent={handleCompleteEvent}
          hasCompletedGame={hasCompletedGame}
          isMember={group?.isMember}
        />
      ) : (
        <div className={`${theme.card} rounded-2xl border ${theme.border} overflow-hidden`}>
          {renderScoringComponent()}
        </div>
      )}

      {/* Scorer confirmation modal */}
      {showScorerConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className={`${theme.card} rounded-2xl border ${theme.border} p-6 max-w-sm w-full shadow-xl`}>
            <h3 className={`text-lg font-bold ${theme.text} mb-2`}>Start Scoring?</h3>
            <p className={`text-sm ${theme.textMuted} mb-6`}>
              You will become the scorer for this game. No one else will be able to score until the game is finished.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowScorerConfirm(false)}
                className={`${btn.base} ${btn.md} ${theme.buttonGhost} flex-1`}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmStartScoring}
                className={`${btn.base} ${btn.md} ${theme.buttonPrimary} flex-1`}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CurrentEventTab;
